import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, Zap } from 'lucide-react';
import api from '../api';



export default function HeroSection() {
  const [banners, setBanners] = useState([]);
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get(`/api/banners`).then(r => {
      if (r.data.length > 0) setBanners(r.data);
    }).catch(() => {});
  }, []);

  // Auto-slide
  useEffect(() => {
    if (banners.length <= 1) return;
    intervalRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(intervalRef.current);
  }, [banners.length]);

  const goTo = (idx) => {
    setCurrent(idx);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % banners.length);
    }, 5000);
  };

  const goPrev = () => goTo((current - 1 + banners.length) % banners.length);
  const goNext = () => goTo((current + 1) % banners.length);

  if (banners.length === 0) {
    return (
      <section id="home" data-testid="hero-section" className="relative min-h-[60svh] sm:min-h-[80vh] flex items-center bg-darkBg">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Enter the World of <span className="neon-text text-neonBlue">Shunya Gatha</span>
          </h1>
          <p className="font-hindi text-xl text-neonGold/80 mb-6">हर दिन नई कहानी, नए एपिसोड</p>
        </div>
      </section>
    );
  }

  const banner = banners[current];

  return (
    <section id="home" data-testid="hero-section" className="relative overflow-hidden bg-darkBg px-2 sm:px-5 lg:px-8">
      <div className="relative mx-auto w-full">
        <div className="relative aspect-video w-full overflow-hidden bg-darkBg sm:rounded-[2rem] border border-white/5 shadow-[0_30px_90px_rgba(0,0,0,0.35)]">
          {/* Blurred side fill so wide screens feel fully covered */}
          <div className="absolute inset-0 scale-110">
            <img
              src={banner.image_url}
              alt=""
              aria-hidden="true"
              className="w-full h-full object-cover object-center blur-2xl scale-110 opacity-45"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-darkBg via-darkBg/35 to-darkBg/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-darkBg via-transparent to-darkBg/55" />
          </div>

          {/* Banner images */}
          <div className="absolute inset-0 z-0 transition-opacity duration-700">
            <img
              src={banner.image_url}
              alt={banner.title}
              className="w-full h-full object-cover object-center"
              data-testid={`hero-banner-img-${current}`}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-darkBg via-darkBg/75 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-darkBg via-darkBg/20 to-darkBg/55" />
          </div>

          {/* Content */}
          <div className="relative z-10 h-full flex items-end sm:items-center">
            <div className="w-full px-3 sm:px-8 lg:px-12 xl:px-16 pb-6 sm:pb-0">
              <div key={current} className="max-w-[15rem] sm:max-w-[620px] transition-all duration-700 sm:pl-12 lg:pl-14 xl:pl-16">
                <span className="inline-block px-3 py-1 text-[9px] sm:px-4 sm:py-1.5 sm:text-xs font-bold uppercase tracking-[0.2em] text-neonBlue border border-neonBlue/30 rounded-full mb-2.5 sm:mb-5 bg-neonBlue/5">
                  Shunya Gatha
                </span>
                <h1 className="font-heading whitespace-normal sm:whitespace-nowrap text-[clamp(1.2rem,6.2vw,2.2rem)] sm:text-[clamp(2rem,4vw,4.5rem)] font-bold text-white leading-[0.92] mb-2 sm:mb-4">
                  {banner.title}
                </h1>
                <p className="font-hindi text-[10px] sm:text-lg lg:text-xl text-neonGold/80 mb-3 sm:mb-8 leading-relaxed max-w-[13.5rem] sm:max-w-lg">
                  {banner.subtitle}
                </p>
                <a
                  href={banner.cta_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-cta-btn"
                  className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto bg-[#2AABEE] text-white font-bold px-3 sm:px-8 py-2 sm:py-3.5 rounded-full hover:bg-[#229ED9] hover:shadow-[0_0_25px_rgba(42,171,238,0.5)] transition-all text-[10px] sm:text-sm"
                >
                  <Zap size={14} />
                  {banner.cta_text}
                </a>
              </div>
            </div>
          </div>

          {/* Desktop navigation arrows */}
          {banners.length > 1 && (
            <>
              <button
                onClick={goPrev}
                data-testid="hero-prev-btn"
                className="hidden sm:flex absolute left-6 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 items-center justify-center text-white/70 hover:text-white hover:bg-black/80 transition-all"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={goNext}
                data-testid="hero-next-btn"
                className="hidden sm:flex absolute right-6 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/60 backdrop-blur-sm border border-white/10 items-center justify-center text-white/70 hover:text-white hover:bg-black/80 transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}

          {/* Dots */}
          {banners.length > 1 && (
            <div className="absolute bottom-4 sm:bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="hero-dots">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  data-testid={`hero-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    i === current ? 'w-8 bg-neonBlue' : 'w-2 bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
        {banners.length > 1 && (
          <div className="sm:hidden mt-3 flex items-center justify-between px-2">
            <button
              onClick={goPrev}
              data-testid="hero-prev-btn-mobile"
              className="w-11 h-11 rounded-full bg-black/70 border border-white/10 flex items-center justify-center text-white/80 shadow-lg"
            >
              <ChevronLeft size={20} />
            </button>
            <div className="flex gap-2">
              {banners.map((_, i) => (
                <button
                  key={i}
                  onClick={() => goTo(i)}
                  data-testid={`hero-dot-mobile-${i}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8 bg-neonBlue' : 'w-2 bg-white/30 hover:bg-white/50'}`}
                />
              ))}
            </div>
            <button
              onClick={goNext}
              data-testid="hero-next-btn-mobile"
              className="w-11 h-11 rounded-full bg-black/70 border border-white/10 flex items-center justify-center text-white/80 shadow-lg"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
